/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { getInfo } from '#utils/UNSAFE_visUtils';
import { useChartDrill } from '#hooks/PRIVATE_useChartDrill';
import { useSelection } from '#hooks/PRIVATE_useVisSelection/useSelection';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { BarItemData } from '#UNSAFE_BarChart/barChart.types';
import { LineAreaItem } from '#UNSAFE_LineAreaChart/lineAreaChart.types';
import { ScatterItem } from '#UNSAFE_ScatterChart/scatterChart.types';

type DrillProps<K, D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>> = Parameters<
  typeof useChartDrill<K, D>
>[0];

type SelectionProps<K, D> = {
  selection: K[];
  onChange?: (detail: { ids: K[]; data: (D | undefined)[] }) => void;
  getDataById: (id: K) => D | undefined;
  ignoreAsTrigger?: (element: EventTarget | null) => boolean;
};

export function useSelectDrill<K, D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>>({
  selectionMode,
  selection,
  onChange,
  rootRef,
  drilling,
  onItemDrill,
  onGroupDrill,
  focusedGroupInfo,
  focusedItemInfo,
  levelsArray,
  getDataItem,
  getDataById,
  ignoreAsTrigger
}: DrillProps<K, D> & SelectionProps<K, D>) {
  const idExtracter = (event: Event): K | undefined => {
    const isItemFocused = focusedItemInfo.isCurrent;
    const info =
      isItemFocused && event.type === 'keyup'
        ? focusedItemInfo
        : getInfo(rootRef, event.target as HTMLElement);
    return info && info.seriesIndex != null && info.groupIndex != null
      ? getDataItem(
          info.seriesIndex,
          info.groupIndex,
          levelsArray[levelsArray.length - 1][info.groupIndex].path
        )?.id
      : undefined;
  };

  const { selectionProps, selectionContent } = useSelection<K, D>({
    idExtracter,
    isDrillEnabled: drilling === 'on',
    selection,
    selectionMode,
    onChange,
    getDataById,
    ignoreAsTrigger
  });

  const drillProps = useChartDrill<K, D>({
    rootRef,
    selectionMode,
    drilling,
    focusedItemInfo,
    focusedGroupInfo,
    levelsArray,
    getDataItem,
    onItemDrill,
    onGroupDrill
  });

  return { selectDrillProps: mergeProps(selectionProps, drillProps), selectionContent };
}
